import { Typography, TypographyProps } from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import React from 'react';

const marqueeHighlightSweep = keyframes`
  0%, 82% {
    transform: translateX(-160%) skewX(-18deg);
    opacity: 0;
  }

  86% {
    opacity: 0.15;
  }

  92% {
    transform: translateX(160%) skewX(-18deg);
    opacity: 0.45;
  }

  100% {
    transform: translateX(160%) skewX(-18deg);
    opacity: 0;
  }
`;

const marqueeHighlightText = keyframes`
  0%, 82%, 100% {
    text-shadow: 0 0 0 rgba(0, 0, 0, 0);
  }

  88% {
    text-shadow: 0 0 12px var(--MarqueeHighlightText-glowSoft);
  }

  92% {
    text-shadow: 0 0 20px var(--MarqueeHighlightText-glowStrong);
  }
`;

export interface MarqueeHighlightTextProps extends TypographyProps {
  durationSeconds?: number;
  highlightColor?: string | ((theme: Theme) => string);
}

const getHighlightColorFromTypographyColor = (theme: Theme, color: TypographyProps['color']) => {
  if (typeof color !== 'string' || color === 'inherit') {
    return undefined;
  }

  if (color === 'textSecondary') {
    return theme.palette.text.secondary;
  }

  const [paletteKey, paletteShade = 'main'] = color.split('.');
  const palette = theme.palette as unknown as Record<string, unknown>;
  const paletteEntry = palette[paletteKey];

  if (paletteEntry && typeof paletteEntry === 'object' && paletteShade in paletteEntry) {
    const resolvedColor = (paletteEntry as Record<string, unknown>)[paletteShade];
    return typeof resolvedColor === 'string' ? resolvedColor : undefined;
  }

  return color;
};

export const MarqueeHighlightText = React.forwardRef<HTMLSpanElement, MarqueeHighlightTextProps>(
  function MarqueeHighlightText({ children, color, durationSeconds = 10, highlightColor, sx, ...props }, ref) {
    const mergedSx: SxProps<Theme> = (theme) => {
      const explicitHighlightColor = typeof highlightColor === 'function' ? highlightColor(theme) : highlightColor;
      const resolvedHighlightColor =
        explicitHighlightColor ?? getHighlightColorFromTypographyColor(theme, color) ?? theme.palette.primary.main;
      const resolvedSx = typeof sx === 'function' ? sx(theme) : sx;
      const sxEntries = Array.isArray(resolvedSx) ? resolvedSx : resolvedSx ? [resolvedSx] : [];

      return sxEntries.reduce<Record<string, unknown>>(
        (accumulator, entry) => {
          if (!entry || Array.isArray(entry)) {
            return accumulator;
          }

          if (typeof entry === 'function') {
            return { ...accumulator, ...entry(theme) };
          }

          return { ...accumulator, ...entry };
        },
        {
          position: 'relative',
          '--MarqueeHighlightText-glowSoft': alpha(resolvedHighlightColor, 0.18),
          '--MarqueeHighlightText-glowStrong': alpha(resolvedHighlightColor, 0.24),
          display: 'inline-flex',
          overflow: 'hidden',
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          animation: `${marqueeHighlightText} ${durationSeconds}s ease-in-out infinite`,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -2,
            bottom: -2,
            width: '40%',
            background: `linear-gradient(90deg, ${alpha(resolvedHighlightColor, 0)} 0%, ${alpha(
              resolvedHighlightColor,
              0.42
            )} 50%, ${alpha(resolvedHighlightColor, 0)} 100%)`,
            transform: 'translateX(-160%) skewX(-18deg)',
            animation: `${marqueeHighlightSweep} ${durationSeconds}s ease-in-out infinite`,
            pointerEvents: 'none',
          },
        }
      );
    };

    return (
      <Typography ref={ref} component="span" color={color} {...props} sx={mergedSx}>
        {children}
      </Typography>
    );
  }
);

MarqueeHighlightText.displayName = 'MarqueeHighlightText';
